import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  X,
  ArrowRight,
  ArrowLeft,
  Check,
  UtensilsCrossed,
  MessageCircle,
  CreditCard,
  Sparkles,
} from 'lucide-react';

/**
 * WelcomeFlowModal — 3-step animated walkthrough shown to a restaurant
 * owner ONCE, right after their first sign-in.
 *
 * Replaces a video-based onboarding (which would require hosting + AV
 * production) with three carefully designed cards. Each card is a
 * micro-illustration built from the brand's gold-on-black palette + Lucide
 * icons + the official NoFee logo so it stays on-brand, not AI-slop.
 *
 * Trigger: rendered by Dashboard on mount when localStorage flag
 *   `nofee_welcome_seen_{restaurant_id}` is missing. Skipping closes the
 *   modal AND persists the flag so we never re-prompt.
 *
 * Props:
 *   open       — boolean, controls visibility
 *   onClose    — called on skip / finish / X
 *   onComplete — called when the user finishes step 3 ("Let's go")
 */
const STEPS = [
  {
    key: 'dish',
    Icon: UtensilsCrossed,
    color: 'from-amber-400 to-yellow-500',
    accent: 'border-yellow-500/40 bg-yellow-500/[0.06] text-yellow-300',
  },
  {
    key: 'channels',
    Icon: MessageCircle,
    color: 'from-emerald-400 to-emerald-600',
    accent: 'border-emerald-500/40 bg-emerald-500/[0.06] text-emerald-300',
  },
  {
    key: 'payments',
    Icon: CreditCard,
    color: 'from-purple-400 to-fuchsia-600',
    accent: 'border-purple-500/40 bg-purple-500/[0.06] text-purple-300',
  },
];

const STORAGE_KEY = (rid) => `nofee_welcome_seen_${rid}`;

const WelcomeFlowModal = ({ open, restaurantId, onClose, onComplete }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);

  // Reset to step 0 every time the modal re-opens so a user who hit "skip"
  // and then re-triggers (e.g. via a future "show me again" button)
  // restarts cleanly.
  useEffect(() => { if (open) setStep(0); }, [open]);

  if (!open) return null;

  const persistSeen = () => {
    try {
      if (restaurantId) localStorage.setItem(STORAGE_KEY(restaurantId), '1');
    } catch (e) { /* private mode / quota — fail silently */ }
  };

  const handleSkip = () => {
    persistSeen();
    onClose?.();
  };

  const handleNext = () => {
    if (step < STEPS.length - 1) {
      setStep(step + 1);
      return;
    }
    // Finish — mark as seen, close, and let the parent decide where to
    // route the user (typically: Continue setup wizard).
    persistSeen();
    onComplete?.();
  };

  const current = STEPS[step];

  return (
    <div
      data-testid="welcome-flow-modal"
      className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={handleSkip}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-md sm:max-w-lg rounded-3xl overflow-hidden border border-yellow-500/30 bg-gradient-to-br from-zinc-950 via-black to-zinc-950 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.85)]"
        style={{ animation: 'nofee-fade-in 360ms cubic-bezier(0.22,1,0.36,1) both' }}
      >
        {/* Skip / close */}
        <button
          type="button"
          onClick={handleSkip}
          data-testid="welcome-flow-skip"
          aria-label={t('welcome_flow.skip', 'Skip')}
          className="absolute top-4 right-4 z-10 w-9 h-9 rounded-full border border-white/10 hover:border-yellow-500/40 hover:bg-white/[0.04] text-white/55 hover:text-yellow-300 flex items-center justify-center transition-colors"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Eyebrow + step counter */}
        <div className="px-7 pt-7 pb-2 flex items-center gap-3">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-yellow-500/30 bg-yellow-500/[0.08]">
            <Sparkles className="w-3 h-3 text-yellow-300" />
            <span className="text-[10.5px] uppercase tracking-[0.22em] text-yellow-300 font-bold">
              {t('welcome_flow.eyebrow', 'Welcome to NoFee')}
            </span>
          </span>
          <span className="text-[11px] text-white/45 font-bold">
            {t('welcome_flow.step_counter', { current: step + 1, total: STEPS.length, defaultValue: `${step + 1} of ${STEPS.length}` })}
          </span>
        </div>

        {/* Illustrated card area — different per step but same height
            so the modal doesn't jump while navigating. */}
        <div className="px-7 py-4">
          <div
            data-testid={`welcome-card-${current.key}`}
            key={current.key}
            className={`relative rounded-2xl border ${current.accent} p-6 sm:p-8 overflow-hidden`}
            style={{ animation: 'nofee-fade-in 360ms cubic-bezier(0.22,1,0.36,1) both' }}
          >
            {/* Decorative orbs to give the card depth without an image */}
            <div className={`absolute -top-12 -right-12 w-48 h-48 rounded-full bg-gradient-to-br ${current.color} opacity-15 blur-3xl pointer-events-none`} />
            <div className={`absolute -bottom-20 -left-12 w-40 h-40 rounded-full bg-gradient-to-br ${current.color} opacity-10 blur-3xl pointer-events-none`} />

            {/* Big animated icon */}
            <div
              className={`relative w-16 h-16 rounded-2xl bg-gradient-to-br ${current.color} text-black flex items-center justify-center shadow-lg`}
              style={{ animation: 'nofee-pill-in 360ms ease-out both' }}
            >
              <current.Icon className="w-8 h-8" strokeWidth={2.2} />
            </div>

            <h3 className="mt-5 font-['Outfit',_Inter,_sans-serif] text-[22px] sm:text-[26px] font-bold tracking-tight text-white leading-snug">
              {t(`welcome_flow.${current.key}_title`)}
            </h3>
            <p className="mt-2 text-[13.5px] text-white/65 leading-relaxed">
              {t(`welcome_flow.${current.key}_body`)}
            </p>

            {/* Three-bullet "what you'll learn" mini-list. We DON'T show
                actual video — these bullets simulate the takeaways the
                walkthrough video would have covered. */}
            <ul className="mt-4 space-y-1.5">
              {[1, 2, 3].map((idx) => (
                <li key={idx} className="flex items-start gap-2 text-[12.5px] text-white/75">
                  <Check className="w-3.5 h-3.5 text-emerald-400 mt-0.5 shrink-0" strokeWidth={2.4} />
                  <span>{t(`welcome_flow.${current.key}_bullet_${idx}`)}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Dots + buttons */}
        <div className="px-7 pb-6 pt-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-1.5" aria-hidden="true">
            {STEPS.map((s, i) => (
              <span
                key={s.key}
                data-testid={`welcome-flow-dot-${i}`}
                className={`h-1.5 rounded-full transition-all ${
                  i === step ? 'w-6 bg-yellow-400' : 'w-1.5 bg-white/15'
                }`}
              />
            ))}
          </div>

          <div className="flex items-center gap-2">
            {step > 0 && (
              <button
                type="button"
                onClick={() => setStep(step - 1)}
                data-testid="welcome-flow-back"
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-white/10 hover:border-white/25 text-white/65 hover:text-white text-[12.5px] font-bold transition-colors"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                {t('welcome_flow.back', 'Back')}
              </button>
            )}
            <button
              type="button"
              onClick={handleNext}
              data-testid="welcome-flow-next"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-b from-yellow-300 to-yellow-500 hover:from-yellow-200 hover:to-yellow-400 text-black text-[13px] font-extrabold tracking-tight shadow-[0_8px_24px_-8px_rgba(250,204,21,0.55)] transition-all hover:-translate-y-0.5"
            >
              {step < STEPS.length - 1
                ? t('welcome_flow.next', 'Next')
                : t('welcome_flow.finish', "Let's go")}
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        {/* Subtle skip link at the very bottom (so it doesn't compete
            visually with the primary CTA). */}
        <div className="border-t border-white/[0.06] px-7 py-3 text-center">
          <button
            type="button"
            onClick={handleSkip}
            data-testid="welcome-flow-skip-link"
            className="text-[11px] uppercase tracking-[0.22em] text-white/40 hover:text-white/70 font-bold transition-colors"
          >
            {t('welcome_flow.skip_link', "I'll explore on my own")}
          </button>
        </div>
      </div>
    </div>
  );
};

WelcomeFlowModal.STORAGE_KEY = STORAGE_KEY;

export default WelcomeFlowModal;
